import { useMemo, useState } from 'react';
import { useTimeline } from '@/lib/hooks/leads/useTimeline';
import { LeadsTimelineEvent } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Phone,
  Mail,
  Calendar,
  FileText,
  CheckCircle,
  ArrowRight,
  Plus,
  Zap,
  Clock,
} from 'lucide-react';

interface TimelinePanelProps {
  contactId?: string;
  companyId?: string;
  dealId?: string;
  limit?: number;
}

const EVENT_CONFIG: Record<
  string,
  { color: string; icon: React.ComponentType<{ className?: string }> }
> = {
  call: { color: '#3b82f6', icon: Phone },
  email: { color: '#10b981', icon: Mail },
  meeting: { color: '#8b5cf6', icon: Calendar },
  note: { color: '#f59e0b', icon: FileText },
  task_completed: { color: '#ef4444', icon: CheckCircle },
  task_created: { color: '#f97316', icon: Plus },
  stage_change: { color: '#6b7280', icon: ArrowRight },
  deal_created: { color: '#3b82f6', icon: Plus },
  custom: { color: '#6b7280', icon: Zap },
};

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 60) return 'just now';
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
}

function TimelineEventItem({ event }: { event: LeadsTimelineEvent }) {
  const config = EVENT_CONFIG[event.eventType] ?? EVENT_CONFIG.custom;
  const Icon = config.icon;

  const badges = useMemo(() => {
    const items: { label: string; value: string }[] = [];
    if (event.contactName) items.push({ label: 'contact', value: event.contactName });
    if (event.companyName) items.push({ label: 'company', value: event.companyName });
    if (event.dealName) items.push({ label: 'deal', value: event.dealName });
    return items;
  }, [event.contactName, event.companyName, event.dealName]);

  return (
    <div className="relative pl-8 pb-6 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-[11px] top-6 bottom-0 w-px bg-border last:hidden" />

      {/* Circle icon */}
      <div
        className="absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full"
        style={{ backgroundColor: config.color }}
      >
        <Icon className="h-3 w-3 text-white" />
      </div>

      {/* Content */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold truncate">{event.title}</span>
          <span className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatRelativeTime(event.occurredAt)}
          </span>
        </div>

        {event.description && (
          <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{event.description}</p>
        )}

        {badges.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {badges.map((b) => (
              <Badge key={b.label} variant="secondary" className="text-[10px] px-1.5 py-0">
                {b.value}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="relative pl-8 pb-6">
          <div className="absolute left-0 top-0.5 h-6 w-6 rounded-full bg-muted animate-pulse" />
          <div className="space-y-2">
            <div className="h-4 w-3/4 rounded bg-muted animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TimelinePanel({ contactId, companyId, dealId, limit = 10 }: TimelinePanelProps) {
  const [displayLimit, setDisplayLimit] = useState(limit);
  const {
    events: displayEvents,
    total: displayTotal,
    loading,
  } = useTimeline({
    contactId,
    companyId,
    dealId,
    limit: displayLimit,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Activity Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        {loading && displayEvents.length === 0 ? (
          <TimelineSkeleton />
        ) : displayEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
        ) : (
          <>
            <div className="relative">
              {displayEvents.map((event) => (
                <TimelineEventItem key={event.id} event={event} />
              ))}
            </div>

            {displayTotal > displayEvents.length && (
              <button
                onClick={() => setDisplayLimit((prev) => prev + limit)}
                disabled={loading}
                className="mt-3 w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1.5 border rounded-md disabled:opacity-50"
              >
                {loading
                  ? 'Loading...'
                  : `Load more (${displayTotal - displayEvents.length} remaining)`}
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
